import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';

// ✅ CONTEXTO COMPARTIDO: WebInfo se carga UNA SOLA VEZ globalmente
const WebInfoContext = createContext(null);

export const WebInfoProvider = ({ children }) => {
  // ✅ Valores por defecto (fallback si no se cargan desde DB)
  const [webInfo, setWebInfo] = useState({
    title: 'Watoolx SAAS',
    description: 'Automatiza, conecta y ¡Vende más!',
    keywords: 'WHATSAPP API',
    companyName: 'watoolx.com'
  });
  
  const [loading, setLoading] = useState(true);
  
  // ✅ Ref para evitar múltiples cargas simultáneas (GLOBAL)
  const loadingRef = useRef(false);
  const isMountedRef = useRef(true);

  // ✅ Actualizar título de la página dinámicamente
  const updatePageTitle = useCallback((title) => {
    if (document.documentElement.dataset.storefront === "true" || window.location.pathname.startsWith('/tienda/')) return;
    if (title) {
      document.title = title;
      const titleElement = document.getElementById('app-title');
      if (titleElement) {
        titleElement.textContent = title;
      }
    }
  }, []);

  // ✅ Actualizar meta tags dinámicamente
  const updatePageMeta = useCallback((description, keywords) => {
    if (document.documentElement.dataset.storefront === "true" || window.location.pathname.startsWith('/tienda/')) return;
    if (description) {
      let metaDescription = document.querySelector('meta[name="description"]');
      if (!metaDescription) {
        metaDescription = document.createElement('meta');
        metaDescription.name = 'description';
        document.head.appendChild(metaDescription);
      }
      metaDescription.content = description;
    }

    if (keywords) {
      let metaKeywords = document.querySelector('meta[name="keywords"]');
      if (!metaKeywords) {
        metaKeywords = document.createElement('meta');
        metaKeywords.name = 'keywords';
        document.head.appendChild(metaKeywords);
      }
      metaKeywords.content = keywords;
    }
  }, []);

  // ✅ Actualizar favicon dinámicamente
  const updateFavicon = useCallback((faviconUrl) => {
    if (!faviconUrl || document.documentElement.dataset.storeFavicon === "1") return;

    let faviconLink = document.querySelector('link[rel="icon"]');
    
    if (!faviconLink) {
      faviconLink = document.createElement('link');
      faviconLink.rel = 'icon';
      document.head.appendChild(faviconLink);
    }

    const currentHref = faviconLink.href;
    const faviconBaseUrl = faviconUrl.split('?')[0];
    
    if (currentHref && currentHref.includes(faviconBaseUrl)) {
      return;
    }

    const timestamp = new Date().getTime();
    const faviconUrlWithCache = `${faviconUrl}?v=${timestamp}`;

    faviconLink.href = faviconUrlWithCache;

    let appleTouchIcon = document.querySelector('link[rel="apple-touch-icon"]');
    if (appleTouchIcon && faviconUrl.includes('apple-touch-icon')) {
      appleTouchIcon.href = faviconUrlWithCache;
    }
  }, []);

  // ✅ Cargar información desde DB (settings globales con companyId = 1)
  // ✅ SOLO SE EJECUTA UNA VEZ GLOBALMENTE
  const loadWebInfo = useCallback(async () => {
    // ✅ Evitar múltiples cargas simultáneas (GLOBAL)
    if (loadingRef.current) {
      return;
    }
    
    loadingRef.current = true;
    
    if (!isMountedRef.current) return;
    
    setLoading(true);

    try {
      // ✅ Verificar si hay token válido
      const token = localStorage.getItem("token");
      const hasToken = token && token !== 'null' && token !== 'undefined' && token.trim() !== '';
      
      let newWebInfo;
      
      if (hasToken) {
        // ✅ Si hay token, usar endpoints individuales (comportamiento original)
        const globalSettingsKeys = [
          'websiteTitle',
          'websiteDescription', 
          'websiteKeyword',
          'companyName'
        ];

        // ✅ Hacer llamadas paralelas para obtener todos los settings
        const settingsPromises = globalSettingsKeys.map(key => 
          api.get(`/settings/${key}`).catch(() => null)
        );

        const settingsResults = await Promise.all(settingsPromises);

        if (!isMountedRef.current) return;

        newWebInfo = {
          title: settingsResults[0]?.data?.value || 'Watoolx SAAS',
          description: settingsResults[1]?.data?.value || 'Automatiza, conecta y ¡Vende más!',
          keywords: settingsResults[2]?.data?.value || 'WHATSAPP API',
          companyName: settingsResults[3]?.data?.value || 'watoolx.com'
        };
      } else {
        // ✅ Si NO hay token, usar endpoint público /settings/web-info
        try {
          const response = await api.get('/settings/web-info');
          if (response.data && response.data.success !== false) {
            newWebInfo = {
              title: response.data.websiteTitle || 'Watoolx SAAS',
              description: response.data.websiteDescription || 'Automatiza, conecta y ¡Vende más!',
              keywords: response.data.websiteKeyword || 'WHATSAPP API',
              companyName: response.data.companyName || 'watoolx.com'
            };
          } else {
            // ✅ Si el endpoint retorna success: false, usar valores por defecto
            newWebInfo = {
              title: 'Watoolx SAAS',
              description: 'Automatiza, conecta y ¡Vende más!',
              keywords: 'WHATSAPP API',
              companyName: 'watoolx.com'
            };
          }
        } catch (publicError) {
          // ✅ Si falla el endpoint público, usar valores por defecto
          console.warn('Error cargando webInfo desde endpoint público, usando valores por defecto:', publicError);
          newWebInfo = {
            title: 'Watoolx SAAS',
            description: 'Automatiza, conecta y ¡Vende más!',
            keywords: 'WHATSAPP API',
            companyName: 'watoolx.com'
          };
        }
      }

      if (!isMountedRef.current) return;

      setWebInfo(newWebInfo);
      
      updatePageTitle(newWebInfo.title);
      updatePageMeta(newWebInfo.description, newWebInfo.keywords);

      // ✅ Cargar favicon solo si hay token válido
      if (hasToken) {
        try {
          const logosResponse = await api.get('/settings/logos');
          if (logosResponse.data?.success && logosResponse.data?.logos) {
            const faviconLogo = logosResponse.data.logos.find(logo => logo.type === 'favicon');
            if (faviconLogo && faviconLogo.url) {
              updateFavicon(faviconLogo.url);
            }
          }
        } catch (faviconError) {
          console.warn('Error cargando favicon desde API:', faviconError);
        }
      }
      
    } catch (error) {
      if (!isMountedRef.current) return;
      console.warn('Error cargando webInfo desde DB, usando valores por defecto:', error);
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
      loadingRef.current = false;
    }
  }, [updatePageTitle, updatePageMeta, updateFavicon]);

  // ✅ Actualizar webInfo cuando cambian los settings (vía socket)
  const updateWebInfo = useCallback((newInfo) => {
    setWebInfo(prevInfo => {
      const updatedInfo = { ...prevInfo, ...newInfo };
      
      if (newInfo.title) {
        updatePageTitle(newInfo.title);
      }
      if (newInfo.description || newInfo.keywords) {
        updatePageMeta(newInfo.description || updatedInfo.description, newInfo.keywords || updatedInfo.keywords);
      }
      
      return updatedInfo;
    });
  }, [updatePageTitle, updatePageMeta]);

  // ✅ Cargar información al montar el provider (SOLO UNA VEZ)
  useEffect(() => {
    isMountedRef.current = true;
    
    const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
    localStorage.setItem('backendUrl', backendUrl);
    
    updatePageTitle(webInfo.title);
    
    // ✅ CARGAR SETTINGS PÚBLICOS UNA SOLA VEZ
    loadWebInfo();
    
    return () => {
      isMountedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // ✅ Array vacío para ejecutar solo una vez

  const value = {
    webInfo,
    loading,
    loadWebInfo,
    updateWebInfo,
    updatePageTitle,
    updatePageMeta,
    updateFavicon
  };

  return (
    <WebInfoContext.Provider value={value}>
      {children}
    </WebInfoContext.Provider>
  );
};

// ✅ Hook para usar el contexto
export const useWebInfo = () => {
  const context = useContext(WebInfoContext);
  if (!context) {
    throw new Error('useWebInfo debe usarse dentro de WebInfoProvider');
  }
  return context;
};

export default WebInfoContext;
